import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppsType, AppItem } from '../../services/apps.service';
import { PhoneFeature } from 'services/phone-features.service';


type ExtensionItem = AppItem & {
  status?: string;
  state?: string;
  extensionDevices?: { networkIp: string; networkPort: string; userAgent: string }[];
  dndEnabled?: boolean;
  forwardBusyEnabled?: boolean;
  forwardNoAnswerEnabled?: boolean;
  forwardImmediateEnabled?: boolean;
};

export enum ExtensionStatus {
  Available = 'Available',
  InCall = 'In Call',
  Ringing = 'Ringing',
  Offline = 'Offline',
}

interface AppsState {
  loading: boolean;
  error: string | null;
  data: {
    conference: AppItem[];
    extension: ExtensionItem[];
    fax_server: AppItem[];
    flow: AppItem[];
    ivr_menu: AppItem[];
    paging_group: AppItem[];
    queue: AppItem[];
    ring_group: AppItem[];
  };
}

const initialState: AppsState = {
  loading: false,
  error: null,
  data: {
    conference: [],
    extension: [],
    fax_server: [],
    flow: [],
    ivr_menu: [],
    paging_group: [],
    queue: [],
    ring_group: [],
  },
};

const appsSlice = createSlice({
  name: 'apps',
  initialState,
  reducers: {
    setAppsLoading: (state) => {
      state.loading = true;
      state.error = null;
    },
    setAppsSuccess: (state, action: PayloadAction<{ [key in AppsType]?: { id: string; name?: string; extension?: string; status?: string; state?: string; }[] }>) => {
      state.loading = false;
      state.error = null;

      (Object.keys(state.data) as AppsType[]).forEach(key => {
        const rawItems = action.payload[key] || [];
        state.data[key] = rawItems.map(item => {
          const baseItem = {
            id: item.id,
            name: item.name || '',
            extension: item.extension || '',
          };

          if (key === 'extension') {
            return {
              ...baseItem,
              ...(item.status && { status: item.status }),
              ...(item.state && { state: item.state }),
            };
          }

          return baseItem;
        });
      });
    },
    setAppsError: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
      (Object.keys(state.data) as AppsType[]).forEach(key => {
        state.data[key] = [];
      });
    },
    clearApps: (state) => {
      state.loading = false;
      state.error = null;
      (Object.keys(state.data) as AppsType[]).forEach(key => {
        state.data[key] = [];
      });
    },
    updateExtensionsObj: (state, action: PayloadAction<Partial<ExtensionItem> & { extension: string }>) => {
      const { extension, ...updateFields } = action.payload;
      const extensionIndex = state.data.extension.findIndex(ext => ext.extension === extension);
      if (extensionIndex !== -1) {
        state.data.extension[extensionIndex] = {
          ...state.data.extension[extensionIndex],
          ...updateFields,
          extension,
        };
      }
    },
    updateMultipleExtensionsObj: (state, action: PayloadAction<{ extensions: { extension: string; extensionDevices: { networkIp: string; networkPort: string; userAgent: string }[] }[] }>) => {
      action.payload.extensions.forEach((extensionData: any) => {
        const extensionIndex = state.data.extension.findIndex(ext => ext.extension == extensionData.extension);
        if (extensionIndex !== -1) {
          state.data.extension[extensionIndex] = {
            ...state.data.extension[extensionIndex],
            ...extensionData,
          };
        }
      });
    },
    addExtensionDevice: (state, action: PayloadAction<{ extension: string; deviceInfo: { networkIp: string; networkPort: string; userAgent: string } }>) => {
      const { extension, deviceInfo } = action.payload;
      const extensionIndex = state.data.extension.findIndex(ext => ext.extension == extension);
      
      if (extensionIndex !== -1) {
        const currentExtension = state.data.extension[extensionIndex];
        const existingDevices = currentExtension.extensionDevices || [];
        
        const deviceExists = existingDevices.some(device => 
          device.networkIp === deviceInfo.networkIp && 
          device.networkPort === deviceInfo.networkPort
        );
        
        if (!deviceExists) {
          state.data.extension[extensionIndex] = {
            ...currentExtension,
            extensionDevices: [...existingDevices, deviceInfo],
            status: 'Available',
          };
        } else {
          state.data.extension[extensionIndex] = {
            ...currentExtension,
            status: 'Available',
          };
        }
      }
    },
    removeExtensionDevice: (state, action: PayloadAction<{ extension: string; deviceInfo: { networkIp?: string; networkPort?: string; userAgent?: string } }>) => {
      const { extension, deviceInfo } = action.payload;
      const extensionIndex = state.data.extension.findIndex(ext => ext.extension == extension);
      
      if (extensionIndex !== -1) {
        const currentExtension = state.data.extension[extensionIndex];
        
        if (currentExtension.extensionDevices && currentExtension.extensionDevices.length > 0) {
          const updatedDevices = currentExtension.extensionDevices.filter(device => {
            const ipMatch = deviceInfo.networkIp ? device.networkIp !== deviceInfo.networkIp : true;
            const portMatch = deviceInfo.networkPort ? device.networkPort !== deviceInfo.networkPort : true;
            const agentMatch = deviceInfo.userAgent ? device.userAgent !== deviceInfo.userAgent : true;
            
            return ipMatch || portMatch || agentMatch;
          });
          
          state.data.extension[extensionIndex] = {
            ...currentExtension,
            extensionDevices: updatedDevices,
            status: updatedDevices.length === 0 ? 'Offline' : currentExtension.status,
          };
        } else {
          state.data.extension[extensionIndex] = {
            ...currentExtension,
            status: 'Offline',
          };
        }
      }
    },
    updateExtensionsPhoneFeatures: (state, action: PayloadAction<PhoneFeature[]>) => {
      action.payload.forEach((phoneFeature) => {
        const extensionNumber = phoneFeature.user;
        const extensionIndex = state.data.extension.findIndex(ext => ext.extension === extensionNumber);
        
        if (extensionIndex !== -1) {
          const currentExtension = state.data.extension[extensionIndex];
          
          state.data.extension[extensionIndex] = {
            ...currentExtension,
            ...phoneFeature.dndEnabled != undefined ? { dndEnabled: phoneFeature.dndEnabled } : {},
            ...phoneFeature.forwardBusyEnabled != undefined ? { forwardBusyEnabled: phoneFeature.forwardBusyEnabled } : {},
            ...phoneFeature.forwardNoAnswerEnabled != undefined ? { forwardNoAnswerEnabled: phoneFeature.forwardNoAnswerEnabled } : {},
            ...phoneFeature.forwardImmediateEnabled != undefined ? { forwardImmediateEnabled: phoneFeature.forwardImmediateEnabled } : {}
          };
        }
      });
    }
  }
});

export const {
  setAppsLoading,
  setAppsSuccess,
  setAppsError,
  clearApps,
  updateExtensionsObj,
  updateMultipleExtensionsObj,
  addExtensionDevice,
  removeExtensionDevice,
  updateExtensionsPhoneFeatures,
} = appsSlice.actions;


export default appsSlice.reducer;
